import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, LimitDeactivation } from '../utils/transactionStore';
const MakerLimitDeactivationTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pending, setPending] = useState<LimitDeactivation[]>([]);
  const [rejected, setRejected] = useState<LimitDeactivation[]>([]);
  const [selected, setSelected] = useState<LimitDeactivation | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setPending(transactionStore.getPendingLimitDeactivations());
    setRejected(transactionStore.getRejectedLimitDeactivations());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: LimitDeactivation, action: 'resubmit' | 'discard') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitLimitDeactivation(selected.id, reason);
        showToast(`Limit deactivation resubmitted for "${selected.category}"`, 'success');
      } else {
        transactionStore.discardLimitDeactivation(selected.id, reason);
        showToast(`Limit deactivation discarded for "${selected.category}"`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const pendingColumns: Column<LimitDeactivation>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1418" data-mp="389lopdlwz1ka">
          {row.id}
        </Typography>
  }, {
    id: 'category',
    label: 'Category',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 260
    }} data-id="element-1419" data-mp="2yb5swxbxz44g">
          {row.category}
        </Typography>
  }, {
    id: 'currentLimit',
    label: 'Current Limit',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1420" data-mp="2pd3os8wwvvcy">
          {row.currency} {row.currentLimit.toLocaleString()}
        </Typography>
  }, {
    id: 'effectiveDate',
    label: 'Effective Date',
    renderCell: row => <Chip label={row.effectiveDate} size="small" sx={{
      fontWeight: 700,
      fontSize: '0.7rem',
      bgcolor: 'rgba(91,78,240,0.1)',
      color: '#5B4EF0'
    }} data-id="element-1421" data-mp="v3f0nt0p4daq" />
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 220
    }} data-id="element-1422" data-mp="14u9o30pohixr">
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1423" data-mp="c6dsjzbuhx2k" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1424" data-mp="q4u01shj7rlr">
          {new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}
        </Typography>
  }];
  const rejectedColumns: Column<LimitDeactivation>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1425" data-mp="201ioluzera1x">
          {row.id}
        </Typography>
  }, {
    id: 'category',
    label: 'Category',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 220
    }} data-id="element-1426" data-mp="2a0rmgyxe98tk">
          {row.category}
        </Typography>
  }, {
    id: 'currentLimit',
    label: 'Current Limit',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1427" data-mp="2izv8obruy4ca">
          {row.currency} {row.currentLimit.toLocaleString()}
        </Typography>
  }, {
    id: 'effectiveDate',
    label: 'Effective Date',
    renderCell: row => <Chip label={row.effectiveDate} size="small" sx={{
      fontWeight: 700,
      fontSize: '0.7rem',
      bgcolor: 'rgba(91,78,240,0.1)',
      color: '#5B4EF0'
    }} data-id="element-1428" data-mp="2sq7u067hy1jh" />
  }, {
    id: 'makerReason',
    label: 'Original Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1429" data-mp="2xqvfydgvbs6u">
          {row.resubmitReason || row.makerReason}
        </Typography>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1430" data-mp="3lco6c5gj3p2a">
          {row.checkerComment}
        </Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1431" data-mp="2xo4y1pxhbca7">
          {new Date(row.checkerActedAt || '').toLocaleString()}
        </Typography>
  }];
  const rejectedRowActions: RowAction<LimitDeactivation>[] = [{
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Delete',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1432" data-mp="2erdav5etgd0v">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1433" data-mp="2jo3ycdsyaawb">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1434" data-mp="2jnc1kznma74g">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1436" data-mp="p9zxyoisk36i">
                <Typography variant="body2" fontWeight={500} data-id="element-1437" data-mp="kflxw8t6p7vi">
                  Pending
                </Typography>
                <Chip label={pending.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1438" data-mp="gdh5teidwjaz" />

              </Box>} data-id="element-1435" data-mp="p8x3y7q69kmd" />

          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1440" data-mp="145nxi0d60242">
                <Typography variant="body2" fontWeight={500} data-id="element-1441" data-mp="z59wh29zdsfj">
                  Rejected
                </Typography>
                <Chip label={rejected.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1442" data-mp="u66vpu11a8dd" />

              </Box>} data-id="element-1439" data-mp="144mmtx029hmv" />

        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Limit Deactivations" data={pending} columns={pendingColumns} keyField="id" emptyStateMessage="No pending limit deactivations" initialVisibleColumns={['category', 'currentLimit', 'effectiveDate', 'makerReason', 'status', 'requestedAt']} data-id="element-1443" data-mp="34ulfwhtrmusp" /> : <DataTable title="Rejected Limit Deactivations" data={rejected} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected limit deactivations" initialVisibleColumns={['category', 'currentLimit', 'effectiveDate', 'makerReason', 'checkerComment', 'checkerActedAt']} data-id="element-1444" data-mp="3nkjmethx0fu8" />}

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1445" data-mp="235rfyx1l0zee">

        <DialogTitle data-id="element-1446" data-mp="due38wbb7zwi">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1447" data-mp="dtuti2vtchtk">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1448" data-mp="8wkv3xqg4vtl" />

            <Typography variant="h6" fontWeight={600} data-id="element-1449" data-mp="ilmdfukgrpaq">
              {actionType === 'resubmit' ? 'Resubmit' : 'Delete'} Limit Deactivation
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1450" data-mp="3m3ectu0xjbiy">
          <Box sx={{
          mt: 1
        }} data-id="element-1451" data-mp="3m45us21ltaqr">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1452" data-mp="3y1ac0nl9q2t">
                  Category: <strong data-id="element-1453" data-mp="3w0ixtqj1h0y6">{selected.category}</strong> &middot; Effective Date: <strong data-id="element-1454" data-mp="3w355dq5xjgge">{selected.effectiveDate}</strong>
                </Typography>

                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1455" data-mp="1fe824d1kmbn5">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1456" data-mp="1baa3s4k2l4v5">
                    Original Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1457" data-mp="1ntozcyg2txuw">
                    {selected.resubmitReason || selected.makerReason}
                  </Typography>

                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1458" data-mp="1duzqfv65rnqs">
                    Checker's Rejection Reason
                  </Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1459" data-mp="21l2dtxz4o5j2">
                    {selected.checkerComment}
                  </Typography>
                </Paper>
              </>}

            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Deleting'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'deleting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1460" data-mp="1ru7rktlvt5xm" />

          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1461" data-mp="2vgue3x9zc1r1">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1462" data-mp="2hicqs0nzkr9n">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1464" data-mp="2cldw9h0euiax" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1465" data-mp="1yt65c5vt5lk5" />} data-id="element-1463" data-mp="2mgxqlyzbt8xk">

            {actionType === 'resubmit' ? 'Resubmit' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerLimitDeactivationTable;